import { useEffect, useRef } from "react";
import { gsap } from "@/animations/gsap";
import { createTextReveal } from "@/animations/textReveal";
import { createFadeUp } from "@/animations/reveal";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

export function Leadership() {
  const ref = useRef<HTMLElement>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) return undefined;
    const ctx = gsap.context(() => {
      createTextReveal(".lead-heading .mask-line > span", ref.current!);
      createFadeUp(".lead-body", ref.current!);
      gsap.fromTo(
        ".lead-rule",
        { scaleX: 0 },
        {
          scaleX: 1,
          transformOrigin: "left center",
          ease: "none",
          scrollTrigger: { trigger: ref.current, start: "top 75%", end: "center 40%", scrub: true },
        }
      );
      gsap.to(".lead-glow", {
        yPercent: 20,
        ease: "none",
        scrollTrigger: { trigger: ref.current, start: "top bottom", end: "bottom top", scrub: true },
      });
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section ref={ref} className="section-y relative overflow-hidden" data-testid="leadership-section">
      <div
        className="lead-glow pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(45% 45% at 80% 20%, var(--accent-muted), transparent 70%)", opacity: 0.22 }}
        aria-hidden="true"
      />
      <div className="container-x relative grid gap-14 lg:grid-cols-12">
        <h2 className="lead-heading font-display text-[clamp(2.5rem,6vw,5.5rem)] font-semibold uppercase leading-[0.98] tracking-tight lg:col-span-8">
          <span className="mask-line">
            <span>LEADERSHIP</span>
          </span>
          <span className="mask-line">
            <span>IS NOT ABOUT</span>
          </span>
          <span className="mask-line">
            <span className="text-outline">HAVING ALL THE ANSWERS.</span>
          </span>
        </h2>
        <div className="flex flex-col justify-end lg:col-span-4 lg:col-start-9">
          <div className="lead-rule mb-8 h-px w-full" style={{ backgroundColor: "var(--accent)" }} aria-hidden="true" />
          <p className="lead-body text-lg leading-relaxed text-foreground/90">
            It is about creating clarity, making technical decisions, helping engineers grow, and keeping the team focused on solving the right problem.
          </p>
        </div>
      </div>
    </section>
  );
}
